<template>
  <ModalChangeLog
    ref="modal"
    :title="$t('setting.list.notification.form.changeHistoryList')"
    @hidden="resetState"
  >
    <div v-if="!filterPage" class="mc-change-log">
      <!-- Sticky Filter Button Header -->
      <div class="mc-change-log-sticky-header" :class="{ 'is-scrolled': isScrolled }">
        <div class="mc-change-log-filter-btn-wrapper">
          <McButton
            icon="icon-outline_filter"
            variant="secondary"
            text=""
            @click="filterPage = true"
          />
          <span v-if="hasActiveFilters" class="mc-change-log-filter-badge"></span>
        </div>
      </div>
      <div
        v-if="changeLog.data.length > 0"
        ref="scrollContainer"
        class="mc-change-log-scroll"
        @scroll="handleScroll"
      >
        <div class="mc-change-log-groups">
          <div
            v-for="(group, gIdx) in changeLog.data"
            :key="'group-' + gIdx"
            class="mc-change-log-group"
            :class="{
              'mc-change-log-group--last': gIdx === changeLog.data.length - 1 && !hasMore,
            }"
          >
            <!-- Date row with icon -->
            <div class="mc-change-log-date-row">
              <span class="mc-change-log-icon">
                <i class="far fa-arrow-alt-circle-right"></i>
              </span>
              <span class="mc-change-log-date-text">
                {{ group.date }}
              </span>
            </div>

            <!-- Entries -->
            <div
              v-for="(item, iIdx) in group.items"
              :key="'item-' + gIdx + '-' + iIdx"
              class="mc-change-log-entry"
            >
              <div class="mc-change-log-entry-time">
                {{ item.time }}
              </div>
              <div class="mc-change-log-entry-content">
                <div class="mc-change-log-title">
                  {{ $t('notification.setting.rule') }} {{ item.title }}
                </div>
                <div class="mc-change-log-text">
                  {{ $t('notification.setting.by') }}: {{ item.author }}
                </div>
                <div class="mc-change-log-text">
                  {{ $t('notification.setting.alertRuleName') }}:
                  <span class="cyan-500" @click="goToNotificationDetail(item.alertRuleId)">
                    {{ item.label }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="mc-change-log-loader"></div>
      </div>
      <div v-else-if="!loading && fetched">
        <McEmptyState
          customFigure="search"
          :title="$t('global.text.notFoundData')"
          :desc="$t('setting.list.notification.resetFilter')"
          class="pb-4"
          :hasButton="false"
        />
      </div>
      <div
        v-if="loading && changeLog.data.length === 0"
        class="mc-change-log-loader text-center py-5"
      >
        {{ $t('global.text.loading') }}...
      </div>
    </div>

    <div v-else class="mc-change-log-filter-page">
      <FormulateForm v-model="filterValues" @submit="applyFilters">
        <!-- Sticky Filter Header -->
        <div
          class="mc-change-log-filter-sticky-header"
          :class="{ 'is-scrolled': isFilterScrolled }"
        >
          <a href="#" @click.prevent="filterPage = false">
            <span class="mc-change-log-filter-back">
              <i class="icon-outline_arrow-left" />
              {{ 'Filter' }}
            </span>
          </a>
        </div>

        <div
          ref="filterScrollContainer"
          class="mc-change-log-filter-content"
          @scroll="handleFilterScroll"
        >
          <div class="mc-change-log-filter-section">
            <FormulateInput
              v-model="form.alertRule"
              type="value-help-v2"
              name="alertRule"
              :placeholder="`${$t('global.form.select')} ${$t(
                'setting.list.notification.form.ruleName'
              )}`"
              class="mc-change-log-filter-input"
              :label="$t('setting.list.notification.form.ruleName')"
              trackBy="alertRuleId"
              :displayedBy="'label'"
              :listGetter="resourcefulQuery.alertRule"
              :tableFields="alertRuleTableFields"
              :fullObject="fullObject.alertRule"
              :multiple="true"
              @update:fullObject="($event) => (fullObject.alertRule = $event)"
            />
            <FormulateInput
              id="alertCode"
              v-model="form.alertCode"
              name="alertCode"
              :label="$t('notification.setting.emailNotification.type')"
              type="value-help-v2"
              :multiple="true"
              :placeholder="`${$t('global.action.choose')} ${$t(
                'notification.setting.emailNotification.type'
              )}`"
              :modalTitle="`${$t('global.form.select')} ${$t(
                'notification.setting.emailNotification.type'
              )}`"
              :listGetter="resourcefulQuery.alertCodes"
              :tableFields="notificationFields"
              :validation-messages="validationMessages.notificationName"
              :displayedBy="'label'"
              trackBy="alertCode"
              :fullObject="fullObject.alertCode"
              @update:fullObject="($event) => (fullObject.alertCode = $event)"
            />
            <FormulateInput
              v-model="form.username"
              type="select-resourceful-v2"
              name="username"
              :placeholder="`${$t('global.form.select')} ${$t('master.user.name')}`"
              class="mc-change-log-filter-input"
              :label="$t('master.user.name')"
              :fullObject="fullObject.username"
              :optionsGetter="resourcefulQuery.userOption"
              :optionLabelFn="({ item }) => item.name"
              trackBy="id"
              @update:fullObject="($event) => (fullObject.username = $event)"
            />

            <FormulateInput
              v-model="form.dateRange"
              type="date-picker"
              class="mc-change-log-filter-input"
              name="dateRange"
              :label="$t('global.object.timeRange')"
              range
              clearable
              datepickerType="datetime"
              append-to-body
              value-type="YYYY-MM-DD HH:mm:ss"
              :formatter="dateFormatter"
            />

            <!-- Urgency Level -->
            <FormulateInput
              type="group"
              name="urgencyLevel"
              class="mc-change-log-filter-input"
              :label="$t('setting.list.notification.form.urgencyLevel')"
            >
              <div class="mc-modal__filter-button-group">
                <button
                  v-for="option in urgencyLevel"
                  :key="option.value"
                  type="button"
                  class="mc-modal__filter-button"
                  :class="{
                    'mc-modal__filter-button--active': selectedUrgencyLevel.includes(option.value),
                  }"
                  @click="toggleUrgencyLevel(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </FormulateInput>

            <!-- Changes Status -->
            <FormulateInput
              type="group"
              name="actionType"
              class="mc-change-log-filter-input"
              :label="$t('setting.list.notification.changesStatus')"
            >
              <div class="mc-modal__filter-button-group">
                <button
                  v-for="option in actionTypeOptions"
                  :key="option.value"
                  type="button"
                  class="mc-modal__filter-button"
                  :class="{
                    'mc-modal__filter-button--active': selectedActionType.includes(option.value),
                  }"
                  @click="toggleActionType(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </FormulateInput>
          </div>
        </div>

        <!-- Filter Footer -->
        <div class="mc-change-log-filter-footer">
          <McButton variant="secondary" :text="$t('global.cancel')" @click="resetFilters" />
          <McButton variant="primary" :text="$t('global.apply')" type="submit" />
        </div>
      </FormulateForm>
    </div>
  </ModalChangeLog>
</template>

<script>
import { GET_NOTIFICATIONS_LIST } from '@/services/api/setting/notification/List'
import { GET_LOG_NOTIFICATIONS } from '@/services/api/setting/notification/Log'
import { GET_USERS } from '@/services/api/master/user'
import ModalChangeLog from './ModalChangeLog.vue'
import McButton from '@/components/McButton'
import McEmptyState from '@/components/McEmptyState'
import i18n from '@/i18n'
import moment from 'moment'
import 'moment/locale/id'
import intensityIconImg from '@/assets/images/notification/intensity.png'

const AlertRuleLabelCol = {
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    description() {
      if (!this.item || !this.item.alert) return ''
      return this.$i18n.locale === 'en'
        ? this.item.alert.descriptionEng
        : this.item.alert.descriptionIdn
    },
    overspeedThresholdText() {
      if (!this.item || this.item.alertCode !== 8) return ''
      if (!this.item.maxThreshold) return ''
      return '· ' + this.item.maxThreshold + ' Km/' + this.$t('global.time.hour')
    },
    overspeedDurationText() {
      if (!this.item || this.item.alertCode !== 8) return ''
      if (this.item.minThresholdTime !== null && this.item.minThresholdTime !== 0) {
        return '· ' + this.item.minThresholdTime + ' ' + this.$t('global.time.seconds')
      }
      return '· Instant'
    },
    intensityTitle() {
      if (!this.item || !this.item.alertCountThreshold || !this.item.alertCountWindow) return ''
      return (
        this.item.alertCountThreshold +
        'x ' +
        this.$t('global.in') +
        ' ' +
        this.item.alertCountWindow +
        ' ' +
        this.$t('global.time.minutes')
      )
    },
    intensityIcon() {
      return intensityIconImg
    },
    rootWithAccess() {
      let ctx = this.$parent
      while (ctx && typeof ctx.hasAccess !== 'function') {
        ctx = ctx.$parent
      }
      return ctx
    },
    canSeeOverspeedThreshold() {
      const ctx = this.rootWithAccess
      if (!ctx) return false
      return ctx.hasAccess('e4c-alert-overspeed-threshold:read')
    },
    canSeeOverspeedDuration() {
      const ctx = this.rootWithAccess
      if (!ctx) return false
      return ctx.hasAccess('e4d-alert-overspeed-threshold-duration:read')
    },
    canSeeUrgentIcon() {
      const ctx = this.rootWithAccess
      if (!ctx) return false
      return ctx.hasAccess(['e4e-alert-center-urgent:read'])
    },
  },
  template: `
    <div class="d-flex justify-content-between">
      <div class="fw-bold text-primary td__noellipsis clickable-field">
        {{ item.label }}
        <p class="fw-normal text-muted">
          {{ description }}
          <template v-if="item.alertCode == 8">
            <template v-if="canSeeOverspeedThreshold">
              {{ overspeedThresholdText }}
            </template>
            <template v-if="canSeeOverspeedDuration">
              {{ overspeedDurationText }}
            </template>
          </template>
        </p>
      </div>
      <div class="mc-alert-rule_label-icon">
        <img
          v-if="item.alertCountThreshold && item.alertCountWindow"
          v-b-tooltip.hover
          :src="intensityIcon"
          alt="intensity"
          :title="intensityTitle"
        />
        <i
          v-if="
            item.alertUrgencyType == 'Urgent' && canSeeUrgentIcon
          "
          v-b-tooltip.hover
          class="mc-icon icon-fill_alert-triangle"
          :title="$t('setting.list.notification.urgentSituation')"
        >
        </i>
      </div>
    </div>
  `,
}

export default {
  name: 'ChangeLogList',

  components: {
    ModalChangeLog,
    McButton,
    McEmptyState,
  },

  props: {
    alertCodes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectedUrgencyLevel: [],
      selectedActionType: [],
      i18n,
      form: {
        username: null,
        alertRule: null,
        alertCode: null,
        dateRange: null,
      },
      fullObject: {
        username: null,
        alertRule: null,
        alertCode: null,
      },
      validationMessages: {
        notificationName: {},
      },
      filterPage: false,
      isScrolled: false,
      isFilterScrolled: false,
      filterValues: {},
      appliedFilters: {},
      changeLog: {
        data: [],
        pagination: {
          page: 1,
          show: 10,
          total: 0,
        },
      },
      fetchedLanguage: localStorage.getItem('language'),
      fetched: false,
      loading: false,
    }
  },
  computed: {
    hasMore() {
      const { page, show, total } = this.changeLog.pagination
      return page * show < total
    },

    hasActiveFilters() {
      if (!this.appliedFilters || Object.keys(this.appliedFilters).length === 0) {
        return false
      }

      return Object.values(this.appliedFilters).some((value) => {
        if (value === null || value === undefined) return false
        if (Array.isArray(value)) return value.length > 0
        if (typeof value === 'string') return value.trim() !== ''
        if (typeof value === 'object') return Object.keys(value).length > 0
        return true
      })
    },

    dateFormatter() {
      const displayFormat = 'DD MMM YYYY HH:mm [WIB]'
      const backendFormat = 'YYYY-MM-DD HH:mm:ss'

      return {
        stringify: (value) => {
          if (!value) return ''

          if (value instanceof Date) {
            return moment(value).format(displayFormat)
          }

          const fromBackend = moment(value, backendFormat, true)
          if (fromBackend.isValid()) {
            return fromBackend.format(displayFormat)
          }

          return moment(value).format(displayFormat)
        },
        parse: (text) => {
          if (!text) return null

          const fromDisplay = moment(text, displayFormat, true)
          if (fromDisplay.isValid()) {
            return fromDisplay.toDate()
          }

          const fromBackend = moment(text, backendFormat, true)
          if (fromBackend.isValid()) {
            return fromBackend.toDate()
          }

          return new Date(text)
        },
      }
    },

    urgencyLevel() {
      return [
        { value: 'Urgent', label: this.$t('report.object.urgent') },
        { value: 'Important', label: this.$t('report.object.important') },
      ]
    },

    actionTypeOptions() {
      return [
        { value: 'Create', label: this.$t('setting.list.notification.created') },
        { value: 'Update', label: this.$t('setting.list.notification.updated') },
        { value: 'Delete', label: this.$t('setting.list.notification.deleted') },
        { value: 'Activate', label: this.$t('setting.list.notification.activated') },
        { value: 'Deactivate', label: this.$t('setting.list.notification.deactivated') },
      ]
    },

    resourcefulQuery() {
      return {
        userOption: async (opts) => {
          let result = {}

          const filter = {
            search: opts?.search || '',
          }

          const res = await this.$apollo.query({
            query: GET_USERS,
            fetchPolicy: 'no-cache',
            variables: {
              filter,
            },
          })

          const users = res.data.users && res.data.users.users ? res.data.users.users : []

          const mappedUsers = users.map((u) => ({
            id: u.userId,
            name: u.fullname,
          }))

          const page = opts?.page || 1
          const limit = opts?.limit || mappedUsers.length || 1
          const total = mappedUsers.length
          const start = (page - 1) * limit
          const end = start + limit
          const paginated = mappedUsers.slice(start, end)

          result = {
            metadata: {
              total_page: total && limit ? Math.ceil(total / limit) : 1,
              total_count: total,
              page,
            },
            data: {
              paginated_result: paginated,
            },
          }

          return result
        },
        alertRule: async (opts) => {
          let result = {}

          const filter = {
            entityType: ['AlertRule'],
            pagination: {
              page: opts.page || 1,
              show: opts.limit || 10,
            },
          }

          if (opts.search) {
            filter.search = opts.search
          }

          const res = await this.$apollo.query({
            query: GET_NOTIFICATIONS_LIST,
            fetchPolicy: 'no-cache',
            variables: {
              filter,
            },
          })

          const root = res.data.alertRules && res.data.alertRules.alertRules
          const rules = root && Array.isArray(root.data) ? root.data : []

          const filteredRules = opts.search
            ? rules.filter((rule) =>
                (rule.label || '').toLowerCase().includes(opts.search.toLowerCase())
              )
            : rules

          const mappedRules = filteredRules.map(
            ({ totalVehicles, totalVehiclesInVgroups, ...rule }) => ({
              ...rule,
              totalVehicles: (totalVehicles ?? 0) + (totalVehiclesInVgroups ?? 0),
            })
          )

          const pagination = root && root.pagination ? root.pagination : {}

          result = {
            metadata: {
              total_page:
                pagination.total && pagination.show
                  ? Math.ceil(pagination.total / pagination.show)
                  : 1,
              total_count: pagination.total || mappedRules.length,
              page: pagination.page || filter.pagination.page,
            },
            data: {
              paginated_result: mappedRules,
            },
          }

          return result
        },
        alertCodes: async (opts) => {
          const page = opts.page || 1
          const limit = opts.limit || 10

          let data = [...this.alertCodes]

          if (opts.search) {
            const searchTerm = opts.search.toLowerCase()
            data = data.filter((code) => {
              const label = this.$i18n.locale === 'id' ? code.descriptionIdn : code.descriptionEng
              return (label || '').toLowerCase().includes(searchTerm)
            })
          }

          const total = data.length
          const start = (page - 1) * limit
          const end = start + limit
          const paginated = data.slice(start, end)

          return {
            metadata: {
              total_page: total && limit ? Math.ceil(total / limit) : 1,
              total_count: total,
              page,
            },
            data: {
              paginated_result: paginated,
            },
          }
        },
      }
    },
    alertRuleTableFields() {
      return [
        {
          key: 'label',
          label: this.$t('setting.list.notification.form.ruleName'),
          isSortable: false,
          isSlot: true,
          slotComponent: AlertRuleLabelCol,
        },
        {
          key: 'totalVehicles',
          label: this.$t('setting.list.notification.form.targetVehicle'),
          isSortable: false,
          thClass: 'mc-table--200',
          formatter: (value) => {
            if (value === 0) {
              return this.$t('setting.list.token.allFleet')
            }
            return value
          },
        },
      ]
    },
    notificationFields() {
      return [
        {
          key: 'label',
          label: this.$t('setting.list.notification.form.notificationName'),
        },
        {
          label: this.$t('global.field.category'),
          key: 'alertCategories',
          formatter: (val) => {
            const key = this.lang == 'en' ? 'categoryEng' : 'categoryIdn'

            return val?.length > 0 ? val[0][key] : ''
          },
        },
      ]
    },
  },

  methods: {
    show() {
      if (localStorage.getItem('language') !== this.fetchedLanguage) {
        this.fetched = false
        this.changeLog = {
          data: [],
          pagination: { page: 1, show: 10, total: 0 },
        }
      }
      if (this.changeLog.data.length === 0) this.fetchData()
      this.$refs.modal.show()
    },

    hide() {
      this.$refs.modal.hide()
    },

    resetState() {
      this.loading = false
      this.isScrolled = false
      this.isFilterScrolled = false
      this.filterPage = false
    },

    handleScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      const threshold = 100

      this.isScrolled = scrollTop > 0

      if (scrollHeight - scrollTop - clientHeight < threshold && !this.loading && this.hasMore) {
        this.fetchData()
      }
    },

    handleFilterScroll(e) {
      this.isFilterScrolled = e.target.scrollTop > 0
    },

    async fetchData() {
      if (this.loading || (!this.hasMore && this.changeLog.pagination.total > 0)) return

      this.loading = true
      this.fetched = true

      try {
        this.fetchedLanguage = localStorage.getItem('language')
        const language = this.fetchedLanguage === 'en' ? 'English' : 'Indonesia'

        const filter = {
          pagination: {
            page: this.changeLog.pagination.page,
            show: this.changeLog.pagination.show,
          },
          language,
        }

        if (
          this.form.alertCode !== null &&
          this.form.alertCode !== undefined &&
          this.form.alertCode.length > 0
        ) {
          filter.alertCode = this.form.alertCode
        }

        if (
          this.form.alertRule !== null &&
          this.form.alertRule !== undefined &&
          this.form.alertRule.length > 0
        ) {
          filter.alertRuleId = this.form.alertRule
        }

        if (this.form.username) {
          filter.userId = [this.form.username]
        }

        if (this.selectedUrgencyLevel && this.selectedUrgencyLevel.length > 0) {
          filter.alertUrgencyType = this.selectedUrgencyLevel
        }

        if (this.selectedActionType && this.selectedActionType.length > 0) {
          filter.changeType = this.selectedActionType
        }

        if (this.appliedFilters && this.appliedFilters.dateRange) {
          const [start, end] = this.appliedFilters.dateRange
          if (start && end) {
            const displayFormat = 'DD MMM YYYY HH:mm [WIB]'

            const startMoment =
              start instanceof Date
                ? moment(start)
                : moment(start, displayFormat, true).isValid()
                ? moment(start, displayFormat, true)
                : moment(start)

            let endMoment =
              end instanceof Date
                ? moment(end)
                : moment(end, displayFormat, true).isValid()
                ? moment(end, displayFormat, true)
                : moment(end)

            if (
              endMoment.hours() === 0 &&
              endMoment.minutes() === 0 &&
              endMoment.seconds() === 0 &&
              endMoment.milliseconds() === 0
            ) {
              endMoment = endMoment.add(1, 'day')
            }

            filter.dateRange = {
              start: startMoment.toISOString(),
              end: endMoment.toISOString(),
            }
          }
        }

        const { data } = await this.$apollo.query({
          query: GET_LOG_NOTIFICATIONS,
          variables: {
            filter,
          },
          fetchPolicy: 'no-cache',
        })

        if (data.alertRuleAuditLogs && data.alertRuleAuditLogs.isSuccessful) {
          const result = data.alertRuleAuditLogs.alertRuleAuditLogs

          if (result && result.data && result.data.length > 0) {
            const mappedData = result.data.map((item) => ({
              date: item.time ? moment(item.time).locale('id').format('DD MMM YYYY') : '-',
              time: item.time ? `${moment(item.time).locale('id').format('HH:mm')} WIB` : '-',
              title: item.actionType,
              author: item.user || '-',
              label: item.label || item.object || '-',
              alertRuleId: item.alertRuleId || null,
            }))

            this.mergeChangelogData(mappedData)
          }

          if (result && result.pagination) {
            this.changeLog.pagination = {
              ...result.pagination,
              page: result.pagination.page + 1,
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch changelog:', error)
      } finally {
        this.loading = false
      }
    },

    mergeChangelogData(newItems) {
      newItems.forEach((item) => {
        const existingGroup = this.changeLog.data.find((g) => g.date === item.date)
        const itemWithExpanded = { ...item, expanded: false }

        if (existingGroup) {
          existingGroup.items.push(itemWithExpanded)
        } else {
          this.changeLog.data.push({
            date: item.date,
            items: [itemWithExpanded],
          })
        }
      })
    },

    toggleExpand(gIdx, iIdx) {
      this.$set(
        this.changeLog.data[gIdx].items[iIdx],
        'expanded',
        !this.changeLog.data[gIdx].items[iIdx].expanded
      )
    },

    applyFilters() {
      this.appliedFilters = { ...this.form }
      this.changeLog = {
        data: [],
        pagination: { page: 1, show: 10, total: 0 },
      }
      this.filterPage = false
      this.fetchData()
      this.$emit('filter-applied', this.appliedFilters)
    },

    resetFilters() {
      this.form = {
        username: null,
        alertRule: null,
        alertCode: null,
        dateRange: null,
      }
      this.fullObject = {
        username: null,
        alertRule: null,
        alertCode: null,
      }
      this.selectedUrgencyLevel = []
      this.selectedActionType = []
      this.filterValues = {}
      this.appliedFilters = {}
      this.changeLog = {
        data: [],
        pagination: { page: 1, show: 10, total: 0 },
      }
      this.fetchData()
      this.$emit('filter-reset')
      this.filterPage = false
    },

    toggleUrgencyLevel(value) {
      const index = this.selectedUrgencyLevel.indexOf(value)
      if (index > -1) {
        this.selectedUrgencyLevel.splice(index, 1)
      } else {
        this.selectedUrgencyLevel.push(value)
      }
    },

    toggleActionType(value) {
      const index = this.selectedActionType.indexOf(value)
      if (index > -1) {
        this.selectedActionType.splice(index, 1)
      } else {
        this.selectedActionType.push(value)
      }
    },

    goToNotificationDetail(alertRuleId) {
      if (!alertRuleId) return

      this.$router.push({
        name: 'Detail Setting Notification',
        params: {
          id: alertRuleId,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './log-style.scss';
</style>
